import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, IconButton, Typography, Box, Button, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

const LoyaltyMilestonePopup: React.FC = () => {
  const [reward, setReward] = useState<any>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token || localStorage.getItem('admin_token')) return;

    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/loyalty/my`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            authkey: AUTH_KEY,
          },
          body: '{}',
        });
        const json = await r.json();
        if (!alive || json?.error) return;

        const rewards = Array.isArray(json?.data?.rewards)
          ? json.data.rewards
          : Array.isArray(json?.data)
            ? json.data
            : [];

        const candidate = rewards.find((x: any) => {
          const status = String(x?.status || '').toLowerCase();
          const id = x?.id ?? x?.issuance_id ?? x?.coupon_id;
          return ['issued', 'sent', 'active'].includes(status) && x?.coupon_code && id && !sessionStorage.getItem(`loyalty-popup-${id}`);
        });

        if (candidate) {
          const id = candidate.id ?? candidate.issuance_id ?? candidate.coupon_id;
          sessionStorage.setItem(`loyalty-popup-${id}`, '1');
          // This is the single coupon that must be auto-applied at checkout.
          localStorage.setItem('loyalty_auto_coupon', String(candidate.coupon_code));
          localStorage.setItem('loyalty_auto_coupon_payload', JSON.stringify(candidate));
          window.dispatchEvent(new CustomEvent('loyalty-coupon-issued', { detail: candidate }));
          setReward(candidate);
        }
      } catch (_) {
        // Keep the popup non-blocking if the loyalty endpoint is temporarily unavailable.
      }
    };

    load();
    const id = window.setInterval(load, 15000);
    return () => { alive = false; window.clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!reward?.end_date) return;
    const tick = () => {
      setSeconds(Math.max(0, Math.floor((new Date(reward.end_date).getTime() - Date.now()) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [reward]);

  const countdown = useMemo(() => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${d}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  }, [seconds]);

  const close = () => setReward(null);

  return (
    <Dialog
      open={!!reward}
      onClose={close}
      aria-labelledby="loyalty-milestone-title"
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', maxWidth: 460, width: 'calc(100% - 32px)' } }}
      BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,.45)' } }}
    >
      <DialogContent sx={{ p: 0, textAlign: 'center' }}>
        <Box sx={{ p: 4, background: 'linear-gradient(135deg,#F76338,#ff9b52)', color: '#fff', position: 'relative' }}>
          <IconButton onClick={close} aria-label="Close" sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
            <CloseIcon />
          </IconButton>
          <Typography id="loyalty-milestone-title" variant="h4" fontWeight={800}>🎉 Congratulations!</Typography>
          <Typography sx={{ mt: 1 }}>
            You completed {reward?.purchase_count ?? reward?.milestone ?? 0} purchases and unlocked a loyalty voucher.
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <LocalOfferIcon sx={{ fontSize: 38, color: '#F76338' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>YOUR VOUCHER</Typography>
          <Typography variant="h5" fontWeight={800} sx={{ my: 1, letterSpacing: 1 }}>{reward?.coupon_code}</Typography>
          <Chip label="Automatically applied at checkout" size="small" sx={{ mb: 2 }} />
          <Typography variant="h4" fontWeight={800}>
            {reward?.currency || 'AED'} {Number(reward?.voucher_amount ?? reward?.amount ?? 0).toFixed(2)}
          </Typography>
          {reward?.end_date && <Typography sx={{ mt: 2, fontWeight: 700 }}>Expires in: {countdown}</Typography>}
          <Button fullWidth variant="contained" sx={{ mt: 3 }} onClick={close}>Got it</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoyaltyMilestonePopup;
